import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, LogOut, Mail, Phone, Calendar, MapPin, Car, Inbox, MessageSquare,
  AlertTriangle, CheckCircle2, Loader2, Lock, RefreshCw, Send, Users, Search,
  Download, Trash2, User, Briefcase, Filter, Truck, Eye, Building2, ClipboardList,
  GraduationCap, Award, DollarSign, Save, UserPlus, X, KeyRound, Plus, Pencil, Sparkles, Send as SendIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { SmsSettingsPanel } from '../components/admin/SmsSettingsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'excelence_admin_token';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };
const fmtBytes = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

// ── Utility hook: debounce a value (for search inputs)
function useDebounce(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

// ── Reusable confirmation dialog (replaces window.confirm)
const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = 'Delete', cancelLabel = 'Cancel', variant = 'danger', onConfirm, testid }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testid}>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-[#0A1628]">
          {variant === 'danger'
            ? <AlertTriangle className="w-5 h-5 text-red-500" />
            : <CheckCircle2 className="w-5 h-5 text-[#2AA8E8]" />}
          {title}
        </AlertDialogTitle>
        <AlertDialogDescription className="text-[#4A5A72]">{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={testid ? `${testid}-cancel` : undefined}>{cancelLabel}</AlertDialogCancel>
        <AlertDialogAction
          data-testid={testid ? `${testid}-confirm` : undefined}
          onClick={onConfirm}
          className={variant === 'danger'
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : 'bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white'}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { value: 'waitlisted', label: 'Waitlisted', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'hired', label: 'Hired', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200' },
];

const AdminLogin = ({ onSuccess }) => {
  const { toast } = useToast();
  const [email, setEmail] = useState('info@excelenceinc.com');
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password: pwd });
      // Persist session — adminToken only present for admin role
      const session = {
        userToken: data.userToken,
        adminToken: data.adminToken || null,
        user: data.user,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      onSuccess(session);
    } catch (err) {
      toast({ title: 'Invalid credentials', description: err?.response?.data?.detail || 'Please check and try again.' });
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4 brand-gradient-tech">
      <div className="absolute inset-0 circuit-bg-dark opacity-40" />
      <div className="relative w-full max-w-md space-y-4">
        <form onSubmit={submit} className="relative w-full bg-white rounded-2xl shadow-2xl p-8 border border-[#E5EAF2]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#0A1628] flex items-center justify-center"><Shield className="w-6 h-6 text-[#2AA8E8]" /></div>
            <div>
              <h1 className="text-xl font-extrabold text-[#0A1628]">Admin Portal</h1>
              <p className="text-sm text-[#4A5A72]">Excelence Inc · Recruitment</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input data-testid="admin-page-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input data-testid="admin-page-password" type="password" autoFocus value={pwd} onChange={e => setPwd(e.target.value)} className="pl-9 h-11" placeholder="Enter admin password" />
              </div>
            </div>
          </div>
          <Button data-testid="admin-page-submit" type="submit" disabled={loading} className="btn-primary mt-5 w-full h-11 font-bold gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in</>}
          </Button>
          <p className="mt-6 text-xs text-center text-[#4A5A72]">Authorised personnel only · MC1766140 · DOT4474028</p>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, 'data-testid': testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-[#E5EAF2] flex items-center gap-4" data-testid={testid}>
    <div className="w-12 h-12 rounded-xl bg-[#0A1628] flex items-center justify-center"><Icon className={`w-6 h-6 ${accent || 'text-[#2AA8E8]'}`} /></div>
    <div>
      <div className="text-2xl font-extrabold text-[#0A1628]">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">{label}</div>
    </div>
  </div>
);

const Pill = ({ children, color }) => (
  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${color || 'bg-[#F7F9FC] text-[#0A1628] border-[#E5EAF2]'}`}>{children}</span>
);

const ProfileModal = ({ app, open, onClose, token }) => {
  if (!app) return null;
  const isDriver = app.applicationType === 'driver';
  const downloadUrl = (i) => `${API}/admin/${isDriver ? 'drivers' : 'waitlist'}/${app.id}/file/${i}?token=${encodeURIComponent(token)}`;
  const statusMeta = STATUS_OPTIONS.find(s => s.value === app.status) || STATUS_OPTIONS[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center font-extrabold">
              {app.firstName?.[0]?.toUpperCase()}{app.lastName?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="text-xl font-extrabold navy-text">{app.firstName} {app.lastName}</div>
              <div className="flex gap-2 mt-1">
                <Pill color={
                  isDriver
                    ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                    : app.track === 'training_waitlist'
                      ? 'bg-orange-50 text-orange-700 border-orange-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                }>
                  {isDriver
                    ? <><Truck className="w-3 h-3 inline mr-1" />Driver</>
                    : app.track === 'training_waitlist'
                      ? <><GraduationCap className="w-3 h-3 inline mr-1" />Training Waitlist</>
                      : <><Briefcase className="w-3 h-3 inline mr-1" />Coordinator</>}
                </Pill>
                <Pill color={statusMeta.color}>{statusMeta.label}</Pill>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="text-[11px] uppercase tracking-wider text-[#4A5A72] font-semibold">Applied · {fmtDate(app.createdAt)}</div>

          <section>
            <h4 className="font-extrabold navy-text mb-2">Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex gap-2"><Mail className="w-4 h-4 text-[#4A5A72]" /><a href={`mailto:${app.email}`} className="text-[#2AA8E8] hover:underline break-all">{app.email}</a></div>
              <div className="flex gap-2"><Phone className="w-4 h-4 text-[#4A5A72]" /><a href={`tel:${app.phone}`}>{app.phone}</a></div>
              {app.location && <div className="flex gap-2 md:col-span-2"><MapPin className="w-4 h-4 text-[#4A5A72]" />{app.location}</div>}
            </div>
          </section>

          {isDriver ? (
            <>
              <section>
                <h4 className="font-extrabold navy-text mb-2">Role & authority</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><strong>Applying as:</strong> {app.applyingAs}</div>
                  <div><strong>Authority:</strong> {app.authorityType === 'excelence' ? 'Excelence Inc MC/DOT' : 'Own MC/DOT'}</div>
                  {app.authorityType === 'own' && (<>
                    <div className="md:col-span-2"><strong>Company:</strong> {app.companyName}</div>
                    <div><strong>MC:</strong> {app.mcNumber}</div>
                    <div><strong>DOT:</strong> {app.dotNumber}</div>
                  </>)}
                </div>
              </section>
              <section>
                <h4 className="font-extrabold navy-text mb-2">Equipment</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><strong>Truck:</strong> {app.equipmentType}</div>
                  <div><strong>Has trailer:</strong> {app.hasTrailer ? 'Yes' : 'No'}</div>
                  {app.hasTrailer && (<>
                    <div><strong>Trailer type:</strong> {app.trailerType}</div>
                    <div><strong>Year:</strong> {app.trailerYear}</div>
                    {app.trailerLength && <div><strong>Length:</strong> {app.trailerLength} ft</div>}
                    {app.carsCapacity && <div><strong>Cars capacity:</strong> {app.carsCapacity}</div>}
                  </>)}
                </div>
              </section>
              <section>
                <h4 className="font-extrabold navy-text mb-2">Experience</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div><strong>Years driving:</strong> {app.experienceYears}</div>
                  <div><strong>Clean record:</strong> {app.cleanRecord ? 'Yes' : 'No'}</div>
                  <div><strong>Available:</strong> {app.startDate}</div>
                </div>
              </section>
            </>
          ) : (
            <section>
              <h4 className="font-extrabold navy-text mb-2">{app.track === 'training_waitlist' ? 'Training waitlist' : 'Experience'}</h4>
              {app.track === 'training_waitlist' ? (
                <div className="space-y-2">
                  {app.whyInterested && (
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-[#4A5A72] mb-1">Why they want to join</div>
                      <div className="bg-[#F7F9FC] border border-[#E5EAF2] rounded-lg p-3 whitespace-pre-wrap">{app.whyInterested}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div><strong>Full-time:</strong> {app.fullTime ? 'Yes' : 'No'}</div>
                    {app.languages && <div><strong>Languages:</strong> {app.languages}</div>}
                    <div><strong>Earliest start:</strong> {app.earliestStartDate || '—'}</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><strong>Years of experience:</strong> {app.yearsExperience || app.experience || '—'}</div>
                  <div><strong>Transportation background:</strong> {app.transportationBefore ? 'Yes' : 'No'}</div>
                  <div><strong>Preferred shift:</strong> {app.preferredShift || '—'}</div>
                  <div><strong>Available start:</strong> {app.startDate || '—'}</div>
                </div>
              )}
            </section>
          )}

          {app.source && (
            <section>
              <h4 className="font-extrabold navy-text mb-2">Where they heard about us</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><strong>Source:</strong> {app.source === 'Other' && app.otherSource ? `${app.source} · ${app.otherSource}` : app.source}</div>
                {app.referralName && <div><strong>Referral name:</strong> {app.referralName}</div>}
                {app.referralPhone && <div><strong>Referral phone:</strong> <a href={`tel:${app.referralPhone}`} className="text-[#2AA8E8]">{app.referralPhone}</a></div>}
              </div>
            </section>
          )}

          {app.files && app.files.length > 0 && (
            <section>
              <h4 className="font-extrabold navy-text mb-2">Attachments ({app.files.length})</h4>
              <div className="space-y-2">
                {app.files.map((f, i) => (
                  <a key={i} href={downloadUrl(i)} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-[#F7F9FC] border border-[#E5EAF2] rounded-lg px-4 py-3 hover:border-[#2AA8E8]">
                    <Download className="w-4 h-4 text-[#2AA8E8]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm navy-text truncate">{f.originalName}</div>
                      <div className="text-xs text-[#4A5A72]">{fmtBytes(f.size)}</div>
                    </div>
                    <span className="text-xs font-bold text-[#2AA8E8]">Download</span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CandidateCard = ({ a, token, onStatus, onDelete, onView, onMoveToCoordinator }) => {
  const isDriver = a.applicationType === 'driver';
  const isTraining = !isDriver && a.track === 'training_waitlist';
  const statusMeta = STATUS_OPTIONS.find(s => s.value === a.status) || STATUS_OPTIONS[0];
  const position = isDriver
    ? `Driver · ${a.applyingAs || 'Owner Operator'}`
    : isTraining
      ? 'Training Candidate'
      : 'Logistics Coordinator';
  const positionColor = isDriver
    ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
    : isTraining
      ? 'bg-orange-50 text-orange-700 border-orange-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const PositionIcon = isDriver ? Truck : isTraining ? GraduationCap : Briefcase;

  return (
    <div
      data-testid={`candidate-card-${a.id}`}
      className="bg-white rounded-2xl border border-[#E5EAF2] p-4 md:p-5 hover:border-[#2AA8E8]/40 hover:shadow-sm transition-all"
    >
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-full bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center font-extrabold shrink-0">
            {a.firstName?.[0]?.toUpperCase()}{a.lastName?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-extrabold text-[#0A1628] text-base md:text-lg truncate">
                {a.firstName} {a.lastName}
              </h3>
              <Pill color={positionColor}>
                <PositionIcon className="w-3 h-3 inline mr-1" />
                {position}
              </Pill>
              <Pill color={statusMeta.color}>{statusMeta.label}</Pill>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#4A5A72]">
              <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 text-[#2AA8E8] hover:underline truncate">
                <Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{a.email}</span>
              </a>
              <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 hover:text-[#0A1628]">
                <Phone className="w-3.5 h-3.5 shrink-0" /> {a.phone}
              </a>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 shrink-0" /> Applied {fmtDate(a.createdAt)}
              </div>
              {a.location && (
                <div className="flex items-center gap-1.5 truncate">
                  <MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{a.location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
          <Select value={a.status} onValueChange={(v) => onStatus(a, v)}>
            <SelectTrigger className="h-9 w-[140px]" data-testid={`candidate-status-${a.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(a)}
            data-testid={`candidate-view-${a.id}`}
            className="h-9 gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">View</span>
          </Button>
          {isTraining && (
            <Button
              size="sm"
              onClick={() => onMoveToCoordinator(a)}
              data-testid={`candidate-move-${a.id}`}
              className="h-9 gap-1.5 bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Move to Coordinator</span>
              <span className="sm:hidden">Promote</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(a)}
            data-testid={`candidate-delete-${a.id}`}
            className="h-9 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>
    </div>
  );
};


// ── User panel components: force-pw-change, dashboard, admin user CRUD
const USER_ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access' },
  { value: 'dispatcher', label: 'Dispatcher', desc: 'Loads + drivers' },
  { value: 'accountant', label: 'Accountant', desc: 'Invoicing + cash flow' },
  { value: 'safety', label: 'Safety', desc: 'Compliance + DVIR' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only dashboards' },
];

const ForceChangePassword = ({ session, onSuccess, onLogout }) => {
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast({ title: 'New password must be at least 6 characters' }); return; }
    if (next !== confirmPw) { toast({ title: 'Passwords do not match' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword: current, newPassword: next }, {
        headers: { 'X-User-Token': session.userToken },
      });
      const updated = { ...session.user, mustChangePassword: false };
      onSuccess(updated);
      toast({ title: 'Password updated ✓' });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4 brand-gradient-tech">
      <div className="absolute inset-0 circuit-bg-dark opacity-40" />
      <form onSubmit={submit} data-testid="force-change-pw-form" className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-[#E5EAF2]">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-amber-600" /></div>
          <div>
            <h1 className="text-xl font-extrabold text-[#0A1628]">Set a new password</h1>
            <p className="text-sm text-[#4A5A72]">First-time login — please change the temp password.</p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Current (temp) password</Label>
            <Input data-testid="force-pw-current" type="password" value={current} onChange={e => setCurrent(e.target.value)} className="h-11 mt-1" required />
          </div>
          <div>
            <Label>New password (min 6 chars)</Label>
            <Input data-testid="force-pw-new" type="password" value={next} onChange={e => setNext(e.target.value)} className="h-11 mt-1" required />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input data-testid="force-pw-confirm" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="h-11 mt-1" required />
          </div>
        </div>
        <Button data-testid="force-pw-submit" disabled={busy} className="btn-primary mt-5 w-full h-11 font-bold gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save & continue'}
        </Button>
        <button type="button" onClick={onLogout} className="mt-4 text-xs w-full text-center text-[#4A5A72] hover:text-[#0A1628]">Sign out</button>
      </form>
    </div>
  );
};

const UserDashboard = ({ session, onLogout }) => {
  const { toast } = useToast();
  const [showPwForm, setShowPwForm] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const user = session.user;
  const roleMeta = USER_ROLES.find(r => r.value === user.role) || USER_ROLES[4];

  const changePw = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast({ title: 'New password must be at least 6 characters' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword: current, newPassword: next }, {
        headers: { 'X-User-Token': session.userToken },
      });
      toast({ title: 'Password updated ✓' });
      setCurrent(''); setNext(''); setShowPwForm(false);
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="user-dashboard">
      <header className="bg-white border-b border-[#E5EAF2] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center"><Shield className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="font-extrabold text-[#0A1628] truncate">Excelence Inc</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5A72]">{roleMeta.label} portal</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="user-logout" className="gap-1.5 h-9">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-full bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center text-xl font-extrabold">
              {user.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-extrabold text-[#0A1628]" data-testid="user-name">{user.name}</h1>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border bg-[#0A1628] text-[#2AA8E8] border-[#0A1628]">
                  {roleMeta.label}
                </span>
              </div>
              <div className="text-sm text-[#4A5A72] mt-1" data-testid="user-email">{user.email}</div>
              {user.phone && <div className="text-sm text-[#4A5A72]">{user.phone}</div>}
              <div className="text-xs text-[#4A5A72] mt-2">Last login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}</div>
            </div>
          </div>
        </div>

        {/* Modules placeholder — Phase 2 will plug AI dispatch / finance / safety here */}
        <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
          <h2 className="font-extrabold text-[#0A1628] mb-1">Your modules</h2>
          <p className="text-sm text-[#4A5A72] mb-4">{roleMeta.desc}. AI modules tailored to your role will appear here.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: Truck, label: 'AI Dispatch', desc: 'Smart load matching · coming soon', enabled: ['admin', 'dispatcher'].includes(user.role) },
              { icon: DollarSign, label: 'AI Finance', desc: 'Auto-invoice + cash flow · coming soon', enabled: ['admin', 'accountant'].includes(user.role) },
              { icon: Shield, label: 'Safety & Compliance', desc: 'DVIR + risk alerts · coming soon', enabled: ['admin', 'safety'].includes(user.role) },
            ].map(m => (
              <div key={m.label} className={`rounded-xl border p-4 ${m.enabled ? 'border-[#E5EAF2] bg-white' : 'border-dashed border-[#E5EAF2] bg-[#F7F9FC] opacity-60'}`}>
                <div className="flex items-center gap-2"><m.icon className="w-4 h-4 text-[#2AA8E8]" /><span className="font-bold text-[#0A1628]">{m.label}</span></div>
                <p className="text-xs text-[#4A5A72] mt-1">{m.desc}</p>
                {m.link && m.enabled && <a href={m.link} className="text-xs font-bold text-[#2AA8E8] hover:underline">Open →</a>}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h2 className="font-extrabold text-[#0A1628]">Account security</h2>
            <Button variant="outline" size="sm" onClick={() => setShowPwForm(s => !s)} data-testid="user-toggle-pw" className="gap-1.5 h-8">
              <KeyRound className="w-3.5 h-3.5" /> {showPwForm ? 'Cancel' : 'Change password'}
            </Button>
          </div>
          {showPwForm && (
            <form onSubmit={changePw} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-testid="user-pw-form">
              <div>
                <Label>Current password</Label>
                <Input data-testid="user-pw-current" type="password" value={current} onChange={e => setCurrent(e.target.value)} className="h-10 mt-1" required />
              </div>
              <div>
                <Label>New password (min 6)</Label>
                <Input data-testid="user-pw-new" type="password" value={next} onChange={e => setNext(e.target.value)} className="h-10 mt-1" required />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button data-testid="user-pw-submit" disabled={busy} className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};


const AdminPage = () => {
  const { toast } = useToast();
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      // New format: JSON {userToken, adminToken, user}. Legacy: plain string admin password.
      if (raw.startsWith('{')) return JSON.parse(raw);
      return { userToken: null, adminToken: raw, user: { role: 'admin', email: 'info@excelenceinc.com', name: 'Admin', mustChangePassword: false } };
    } catch { return null; }
  });
  const token = session?.adminToken || null;
  const setToken = (v) => {
    if (v === null) { localStorage.removeItem(STORAGE_KEY); setSession(null); }
    else if (typeof v === 'object') { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); setSession(v); }
  };
  const [tab, setTab] = useState('training'); // training | coordinator | driver | quotes | contacts | pricing
  const [statusFilter, setStatusFilter] = useState('all');
  const [applications, setApplications] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sources, setSources] = useState([]);
  const [viewApp, setViewApp] = useState(null);

  // Confirmation dialog state — single instance used for all destructive/transitional actions
  const [confirm, setConfirm] = useState(null); // { title, description, confirmLabel, variant, onConfirm, testid }
  const closeConfirm = useCallback(() => setConfirm(null), []);

  const headers = useMemo(() => ({ 'X-Admin-Password': token }), [token]);

  const logout = () => { setToken(null); };

  const loadStats = async () => {
    try { const { data } = await axios.get(`${API}/admin/stats`, { headers }); setStats(data); }
    catch (err) { if (err?.response?.status === 401) logout(); }
  };

  const loadSources = async () => {
    try { const { data } = await axios.get(`${API}/admin/sources`, { headers }); setSources(data); }
    catch (err) {
      if (err?.response?.status === 401) logout();
      else if (process.env.NODE_ENV === 'development') console.debug('loadSources failed', err);
    }
  };

  const loadApplications = async () => {
    const params = {};
    if (tab === 'training' || tab === 'coordinator') {
      params.type = 'coordinator';
      params.track = tab === 'training' ? 'training_waitlist' : 'experienced';
    } else if (tab === 'driver') {
      params.type = 'driver';
    } else {
      return;
    }
    if (sourceFilter && sourceFilter !== 'all') params.source = sourceFilter;
    if (debouncedSearch) params.q = debouncedSearch;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (fromDate) params.fromDate = new Date(fromDate).toISOString();
    if (toDate) params.toDate = new Date(toDate + 'T23:59:59').toISOString();
    try {
      const { data } = await axios.get(`${API}/admin/applications`, { headers, params });
      setApplications(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const loadOther = async () => {
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setQuotes(q.data); setContacts(c.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else if (process.env.NODE_ENV === 'development') console.debug('loadOther failed', err);
    }
  };

  const loadAll = async () => {
    if (!token) return;
    setLoading(true);
    await Promise.all([loadStats(), loadApplications(), loadOther(), loadSources()]);
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (token && ['training', 'coordinator', 'driver'].includes(tab)) loadApplications(); /* eslint-disable-next-line */ }, [tab, statusFilter, sourceFilter, debouncedSearch, fromDate, toDate]);

  // Filtered quotes (client-side search + date filter so typing stays snappy)
  const filteredQuotes = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate).getTime() : null;
    const to = toDate ? new Date(toDate + 'T23:59:59').getTime() : null;
    return quotes.filter(x => {
      if (q) {
        const hay = `${x.name || ''} ${x.email || ''} ${x.phone || ''} ${x.trackingCode || ''} ${x.superDispatchId || ''} ${x.fromLocation || ''} ${x.toLocation || ''} ${x.vehicleType || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from || to) {
        const t = new Date(x.createdAt).getTime();
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      return true;
    });
  }, [quotes, debouncedSearch, fromDate, toDate]);

  const testEmail = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/test-email`, {}, { headers });
      if (data.ok) toast({ title: 'Test email sent!', description: 'Check info@excelenceinc.com.' });
      else toast({ title: 'Email failed' });
    } catch (err) { toast({ title: 'Request failed' }); }
  };

  const onChangeStatus = async (a, status) => {
    try {
      const url = a.applicationType === 'driver' ? `${API}/admin/drivers/${a.id}` : `${API}/admin/waitlist/${a.id}`;
      await axios.patch(url, { status }, { headers });
      setApplications(prev => prev.map(x => x.id === a.id ? { ...x, status } : x));
      loadStats();
      toast({ title: 'Status updated' });
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || '' }); }
  };

  const onDeleteApp = (a) => {
    setConfirm({
      title: 'Are you sure you want to delete this item?',
      description: `This will permanently remove the application from ${a.firstName} ${a.lastName} and any uploaded files. This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      testid: 'delete-candidate-confirm',
      onConfirm: async () => {
        closeConfirm();
        try {
          const url = a.applicationType === 'driver' ? `${API}/admin/drivers/${a.id}` : `${API}/admin/waitlist/${a.id}`;
          await axios.delete(url, { headers });
          setApplications(prev => prev.filter(x => x.id !== a.id));
          loadStats();
          toast({ title: 'Application deleted' });
        } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' }); }
      },
    });
  };

  const onMoveToCoordinator = (a) => {
    setConfirm({
      title: 'Promote to Logistics Coordinator?',
      description: `${a.firstName} ${a.lastName} will be moved from Training Candidates to Logistics Coordinators and their status will be set to Hired.`,
      confirmLabel: 'Move & mark hired',
      cancelLabel: 'Cancel',
      variant: 'primary',
      testid: 'move-to-coordinator-confirm',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.patch(
            `${API}/admin/waitlist/${a.id}`,
            { track: 'experienced', status: 'hired' },
            { headers },
          );
          // Since we're on the training tab, remove this candidate from the local list
          setApplications(prev => prev.filter(x => x.id !== a.id));
          loadStats();
          toast({ title: 'Moved to Logistics Coordinators ✓', description: `${a.firstName} ${a.lastName} is now marked Hired.` });
        } catch (err) {
          toast({ title: 'Move failed', description: err?.response?.data?.detail || '' });
        }
      },
    });
  };

  const onDeleteQuote = (q) => {
    setConfirm({
      title: 'Are you sure you want to delete this item?',
      description: `Quote ${q.trackingCode || q.id} from ${q.name || 'customer'} will be permanently removed. This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      testid: 'delete-quote-confirm',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/admin/quotes/${q.id}`, { headers });
          setQuotes(prev => prev.filter(x => x.id !== q.id));
          loadStats();
          toast({ title: 'Quote deleted' });
        } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' }); }
      },
    });
  };

  if (!session) return <AdminLogin onSuccess={setToken} />;

  // Force password change before anything else
  if (session.user?.mustChangePassword) {
    return <ForceChangePassword session={session} onSuccess={(updated) => setToken({ ...session, user: updated })} onLogout={logout} />;
  }

  // Non-admin roles get a focused user dashboard (not the full /admin tools)
  if (session.user?.role !== 'admin') {
    return <UserDashboard session={session} onLogout={logout} />;
  }

  if (!token) return <AdminLogin onSuccess={setToken} />;

  const trainingCount = stats.coordinatorsTraining ?? 0;
  const coordinatorCount = stats.coordinatorsExperienced ?? 0;
  const driversCount = stats.drivers ?? 0;
  const quotesCount = stats.quotes ?? 0;
  const messagesCount = stats.contacts ?? 0;

  const mainTabs = [
    { id: 'training', label: 'Training', icon: GraduationCap, count: trainingCount },
    { id: 'coordinator', label: 'Coordinators', icon: Briefcase, count: coordinatorCount },
    { id: 'driver', label: 'Drivers', icon: Truck, count: driversCount },
    { id: 'quotes', label: 'Quotes', icon: Inbox, count: quotesCount },
    { id: 'contacts', label: 'Messages', icon: MessageSquare, count: messagesCount },
    { id: 'brokers', label: 'Brokers', icon: Building2, count: null },
    { id: 'brain', label: 'AI Brain', icon: Sparkles, count: null },
    { id: 'users', label: 'Users', icon: Users, count: null },
    { id: 'pricing', label: 'Pricing', icon: DollarSign, count: null },
  ];

  const isApplicationsTab = ['training', 'coordinator', 'driver'].includes(tab);
  const applicationsEmptyTitle = tab === 'training' ? 'No training candidates yet' : tab === 'coordinator' ? 'No coordinator applicants yet' : 'No driver applications yet';
  const searchPlaceholder = tab === 'quotes'
    ? 'Search by tracking #, name, email, route, vehicle…'
    : 'Search by name, phone, email, MC / DOT…';
  const showFiltersBar = isApplicationsTab || tab === 'quotes';

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <header className="bg-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#2AA8E8]" />
            <span className="font-extrabold">Excelence Inc Admin</span>
            <span className="hidden md:inline text-xs text-white/50 ml-2">MC1766140 · DOT4474028</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={testEmail} className="text-white border-white/20 bg-white/5 hover:bg-white hover:text-[#0A1628] gap-1"><Send className="w-3.5 h-3.5" /> Test email</Button>
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} className="text-white border-white/20 bg-white/5 hover:bg-white hover:text-[#0A1628] gap-1"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={logout} className="text-white border-white/20 bg-white/5 hover:bg-white hover:text-[#0A1628] gap-1"><LogOut className="w-3.5 h-3.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard data-testid="stat-training" icon={GraduationCap} label="Training Candidates" value={trainingCount} accent="text-orange-400" />
          <StatCard data-testid="stat-coordinators" icon={Briefcase} label="Coordinators" value={coordinatorCount} accent="text-[#D4A24C]" />
          <StatCard data-testid="stat-drivers" icon={Truck} label="Drivers" value={driversCount} accent="text-[#2AA8E8]" />
          <StatCard data-testid="stat-quotes" icon={Inbox} label="Quotes" value={quotesCount} accent="text-[#D4A24C]" />
          <StatCard data-testid="stat-messages" icon={MessageSquare} label="Messages" value={messagesCount} accent="text-[#2AA8E8]" />
        </div>

        {/* Main Tabs */}
        <div className="flex items-center gap-2 mb-4 border-b border-[#E5EAF2] overflow-x-auto no-scrollbar">
          {mainTabs.map(t => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-3 md:px-4 py-3 font-bold text-sm transition-colors relative flex items-center gap-2 whitespace-nowrap ${tab === t.id ? 'text-[#0A1628]' : 'text-[#4A5A72] hover:text-[#0A1628]'}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count !== null && <span className="text-xs text-[#4A5A72]">({t.count})</span>}
              {tab === t.id && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#2AA8E8]" />}
            </button>
          ))}
        </div>

        {/* Unified filters bar — applications + quotes */}
        {showFiltersBar && (
          <div className="bg-white rounded-2xl border border-[#E5EAF2] p-4 mb-4 space-y-3">
            {/* Status filter (applications only) */}
            {isApplicationsTab && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#4A5A72] mr-1">Status:</span>
                {[
                  { value: 'all', label: 'All' },
                  ...STATUS_OPTIONS,
                ].map(s => (
                  <button
                    key={s.value}
                    data-testid={`status-filter-${s.value}`}
                    onClick={() => setStatusFilter(s.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${statusFilter === s.value ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-[#0A1628] border-[#E5EAF2] hover:border-[#2AA8E8]'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Source filter (applications only) */}
            {isApplicationsTab && sources.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F0F3F8]">
                <span className="text-xs font-bold uppercase tracking-wider text-[#4A5A72] mr-1">Source:</span>
                <button
                  onClick={() => setSourceFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${sourceFilter === 'all' ? 'bg-[#0A1628] text-white border-[#0A1628]' : 'bg-white text-[#0A1628] border-[#E5EAF2] hover:border-[#2AA8E8]'}`}
                >
                  All
                </button>
                {sources.map(s => (
                  <button
                    key={s.source}
                    onClick={() => setSourceFilter(s.source)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors flex items-center gap-1 ${sourceFilter === s.source ? 'bg-[#D4A24C] text-[#0A1628] border-[#D4A24C]' : 'bg-white text-[#0A1628] border-[#E5EAF2] hover:border-[#D4A24C]'}`}
                  >
                    {s.source} <span className="opacity-70">({s.count})</span>
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input
                  data-testid="admin-search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9 h-10"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-[#4A5A72] shrink-0" />
                <Input
                  data-testid="admin-filter-from"
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="w-auto h-10"
                  title="From"
                />
                <Input
                  data-testid="admin-filter-to"
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="w-auto h-10"
                  title="To"
                />
                {(search || statusFilter !== 'all' || fromDate || toDate || sourceFilter !== 'all') && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="admin-filter-clear"
                    onClick={() => { setSearch(''); setStatusFilter('all'); setFromDate(''); setToDate(''); setSourceFilter('all'); }}
                    className="gap-1"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Applications — card view */}
        {isApplicationsTab && (
          <div className="space-y-3" data-testid={`applications-list-${tab}`}>
            {loading ? (
              <div className="text-center py-16 text-[#4A5A72] bg-white rounded-2xl border border-[#E5EAF2]">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#E5EAF2]">
                <User className="w-10 h-10 text-[#4A5A72] mx-auto mb-2" />
                <p className="font-bold text-[#0A1628]">{applicationsEmptyTitle}</p>
                <p className="text-sm text-[#4A5A72]">Try adjusting filters.</p>
              </div>
            ) : (
              applications.map(a => (
                <CandidateCard
                  key={a.id}
                  a={a}
                  token={token}
                  onStatus={onChangeStatus}
                  onDelete={onDeleteApp}
                  onView={setViewApp}
                  onMoveToCoordinator={onMoveToCoordinator}
                />
              ))
            )}
          </div>
        )}

        {tab === 'quotes' && (
          <div className="space-y-3">
            <ImportSuperDispatchPanel token={token} onImported={loadOther} />
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#E5EAF2]">
                <Inbox className="w-10 h-10 text-[#4A5A72] mx-auto mb-2" />
                <p className="font-bold text-[#0A1628]">{quotes.length === 0 ? 'No quotes yet' : 'No quotes match your filters'}</p>
              </div>
            ) : filteredQuotes.map(q => (
              <QuoteAdminCard key={q.id} q={q} token={token} onRefresh={loadOther} onDelete={() => onDeleteQuote(q)} />
            ))}
          </div>
        )}

        {tab === 'contacts' && (
          <div className="space-y-3">
            {contacts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#E5EAF2]"><MessageSquare className="w-10 h-10 text-[#4A5A72] mx-auto mb-2" /><p className="font-bold text-[#0A1628]">No messages yet</p></div>
            ) : contacts.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-[#E5EAF2] p-5">
                <div className="flex justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#4A5A72] font-semibold">Message · {fmtDate(c.createdAt)}</div>
                    <div className="font-extrabold text-lg text-[#0A1628]">{c.firstName} {c.lastName}</div>
                  </div>
                </div>
                <a href={`mailto:${c.email}`} className="text-sm text-[#2AA8E8] hover:underline">{c.email}</a>
                <div className="bg-[#F7F9FC] rounded-lg p-4 text-sm mt-3 whitespace-pre-wrap">{c.message}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'pricing' && <PricingSettingsPanel token={token} />}
        {tab === 'brokers' && <BrokersAdminPanel token={token} onConfirm={setConfirm} closeConfirm={closeConfirm} />}
        {tab === 'brain' && <BrainChatPanel token={token} />}
        {tab === 'users' && <UsersAdminPanel token={token} session={session} onConfirm={setConfirm} closeConfirm={closeConfirm} />}

        <ProfileModal app={viewApp} open={!!viewApp} onClose={() => setViewApp(null)} token={token} />

        {/* Global confirmation dialog */}
        <ConfirmDialog
          open={!!confirm}
          onOpenChange={(v) => { if (!v) closeConfirm(); }}
          title={confirm?.title || ''}
          description={confirm?.description || ''}
          confirmLabel={confirm?.confirmLabel}
          cancelLabel={confirm?.cancelLabel}
          variant={confirm?.variant}
          testid={confirm?.testid}
          onConfirm={() => confirm?.onConfirm?.()}
        />
      </main>
    </div>
  );
};

const SHIPMENT_STATUS_OPTIONS = [
  { id: 'quote_received', label: 'Quote received' },
  { id: 'pending_dispatch', label: 'Pending dispatch' },
  { id: 'posted', label: 'Posted to loadboard' },
  { id: 'carrier_assigned', label: 'Carrier assigned' },
  { id: 'in_transit', label: 'In transit' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
];

const QuoteAdminCard = ({ q, token, onRefresh, onDelete }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(q.status || 'quote_received');
  const [sdId, setSdId] = useState(q.superDispatchId || null);
  const [attachInput, setAttachInput] = useState('');
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState(false);

  const updateStatus = async (newStatus) => {
    setBusy('status');
    try {
      await axios.patch(`${API}/admin/quotes/${q.id}/status`, { status: newStatus }, { headers: { 'X-Admin-Password': token } });
      setStatus(newStatus);
      toast({ title: 'Status updated', description: SHIPMENT_STATUS_OPTIONS.find(o => o.id === newStatus)?.label });
    } catch (err) {
      toast({ title: 'Failed to update status', description: err?.response?.data?.detail || '' });
    } finally { setBusy(''); }
  };

  const attachLoadId = async () => {
    const v = attachInput.trim();
    if (!v) { toast({ title: 'Enter a Load ID' }); return; }
    setBusy('attach');
    try {
      const { data } = await axios.patch(
        `${API}/admin/quotes/${q.id}/super-dispatch-id`,
        { superDispatchId: v },
        { headers: { 'X-Admin-Password': token } },
      );
      setSdId(data.superDispatchId);
      setStatus('posted');
      setAttachInput('');
      toast({ title: 'Load ID attached ✓', description: `Quote is now trackable via ${data.superDispatchId}` });
      if (onRefresh) onRefresh();
    } catch (err) {
      toast({ title: 'Attach failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(''); }
  };

  const postToSuperDispatch = async () => {
    setBusy('post');
    try {
      const { data } = await axios.post(`${API}/admin/super-dispatch/post/${q.id}`, {}, { headers: { 'X-Admin-Password': token } });
      if (data.ok) {
        setSdId(data.sdId);
        setStatus('posted');
        toast({
          title: data.live ? 'Posted to Super Dispatch ✓' : 'Posted (stub mode)',
          description: data.live ? `Order ID: ${data.sdId}` : 'SUPERDISPATCH_API_KEY not set — using stub response.',
        });
      } else {
        toast({ title: 'Super Dispatch post failed', description: data.error || '' });
      }
    } catch (err) {
      toast({ title: 'Super Dispatch post failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(''); }
  };

  const syncSuperDispatch = async () => {
    setBusy('sync');
    try {
      const { data } = await axios.post(`${API}/admin/super-dispatch/sync/${q.id}`, {}, { headers: { 'X-Admin-Password': token } });
      toast({ title: 'Synced', description: `Status: ${data.status}${!data.live ? ' (stub)' : ''}` });
    } catch (err) {
      toast({ title: 'Sync failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(''); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5EAF2] p-4 md:p-5 hover:border-[#2AA8E8]/40 transition-all" data-testid={`admin-quote-card-${q.id}`}>
      {/* Top row — compact summary */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {q.trackingCode && (
              <code data-testid={`quote-tracking-${q.id}`} className="bg-[#0A1628] text-[#2AA8E8] px-2 py-1 rounded text-xs font-bold tracking-wider">
                {q.trackingCode}
              </code>
            )}
            {sdId && <Pill color="bg-[#0A1628] text-white border-[#0A1628]">Load #{sdId}</Pill>}
            <Pill color={q.emailSent ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
              {q.emailSent ? 'Email sent' : 'Email pending'}
            </Pill>
          </div>
          <div className="font-extrabold text-lg text-[#0A1628] mb-1 truncate">{q.name || '—'}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#4A5A72]">
            <div className="flex items-center gap-1.5 truncate">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate"><strong className="text-[#0A1628]">{q.fromLocation}</strong> → <strong className="text-[#0A1628]">{q.toLocation}</strong></span>
            </div>
            <div className="flex items-center gap-1.5">
              <Car className="w-3.5 h-3.5 shrink-0" />
              <span>{q.vehicleType} · {q.trailer}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span>{fmtDate(q.createdAt)}</span>
            </div>
            {q.email && (
              <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-[#2AA8E8] hover:underline truncate">
                <Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{q.email}</span>
              </a>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(v => !v)}
            data-testid={`quote-view-${q.id}`}
            className="h-9 gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{expanded ? 'Hide' : 'View'}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            data-testid={`quote-delete-${q.id}`}
            className="h-9 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>

      {/* Expanded: full contact + SD controls */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-[#E5EAF2] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><Mail className="inline w-4 h-4 text-[#4A5A72] mr-2" /><a href={`mailto:${q.email}`} className="text-[#2AA8E8] hover:underline">{q.email}</a></div>
            <div><Phone className="inline w-4 h-4 text-[#4A5A72] mr-2" />{q.phone}</div>
            {q.estimate && (
              <div className="md:col-span-2 bg-[#F7F9FC] rounded-lg px-3 py-2 text-xs">
                <strong className="text-[#0A1628]">Estimate:</strong> ${q.estimate.totalMin} – ${q.estimate.totalMax} · {q.estimate.distanceMiles} mi · ETA {q.estimate.transitWindow}
              </div>
            )}
            {(q.driverName || q.vin || q.trackingUrl || q.bolFilePath) && (
              <div className="md:col-span-2 bg-[#0A1628] rounded-lg px-4 py-3 text-xs text-white/90 space-y-1.5" data-testid={`quote-bol-${q.id}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2AA8E8] mb-1">BOL · Super Dispatch</div>
                {q.driverName && <div>Driver: <strong className="text-white">{q.driverName}</strong>{q.driverPhone ? ` · ${q.driverPhone}` : ''}</div>}
                {q.vin && <div>VIN: <code className="text-[#D4A24C]">{q.vin}</code></div>}
                {q.trackingUrl && (
                  <a
                    href={q.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`quote-sd-tracking-${q.id}`}
                    className="inline-flex items-center gap-1.5 text-[#2AA8E8] hover:underline font-bold"
                  >
                    Open SD live tracking →
                  </a>
                )}
                {q.bolFilePath && (
                  <a
                    href={`${API}/admin/quotes/${q.id}/bol?token=${encodeURIComponent(token)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`quote-bol-download-${q.id}`}
                    className="inline-flex items-center gap-1.5 text-[#D4A24C] hover:underline font-bold ml-4"
                  >
                    <Download className="w-3 h-3" /> Download BOL PDF
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[180px]">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Shipment status</Label>
              <select
                data-testid={`quote-status-select-${q.id}`}
                value={status}
                disabled={busy === 'status'}
                onChange={(e) => updateStatus(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-[#E5EAF2] bg-white px-3 text-sm font-bold text-[#0A1628] focus:outline-none focus:border-[#2AA8E8]"
              >
                {SHIPMENT_STATUS_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={busy === 'post'}
              onClick={postToSuperDispatch}
              data-testid={`sd-post-${q.id}`}
              className="gap-2 border-[#0A1628] text-[#0A1628] hover:bg-[#0A1628] hover:text-white h-9"
            >
              {busy === 'post' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sdId ? 'Re-post to Super Dispatch' : 'Post to Super Dispatch'}
            </Button>
            {sdId && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy === 'sync'}
                onClick={syncSuperDispatch}
                data-testid={`sd-sync-${q.id}`}
                className="gap-2 h-9"
              >
                {busy === 'sync' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Sync status
              </Button>
            )}
            {!sdId && (
              <div className="flex items-end gap-2">
                <div className="min-w-[180px]">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Attach real SD Load ID</Label>
                  <Input
                    data-testid={`sd-attach-input-${q.id}`}
                    value={attachInput}
                    onChange={(e) => setAttachInput(e.target.value)}
                    placeholder="e.g. 21865599"
                    className="h-9 mt-1"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === 'attach' || !attachInput.trim()}
                  onClick={attachLoadId}
                  data-testid={`sd-attach-btn-${q.id}`}
                  className="gap-2 h-9 border-[#2AA8E8] text-[#2AA8E8] hover:bg-[#2AA8E8] hover:text-white"
                >
                  {busy === 'attach' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Attach
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ImportSuperDispatchPanel = ({ token, onImported }) => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [bolFile, setBolFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    superDispatchId: '',
    fromLocation: '',
    toLocation: '',
    vehicleType: 'Sedan',
    trailer: 'open',
    condition: 'running',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    driverName: '',
    driverPhone: '',
    trackingUrl: '',
    vin: '',
    status: 'pending_dispatch',
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const resetForm = () => {
    setBolFile(null);
    setForm({
      superDispatchId: '', fromLocation: '', toLocation: '', vehicleType: 'Sedan',
      trailer: 'open', condition: 'running', customerName: '', customerEmail: '',
      customerPhone: '', driverName: '', driverPhone: '', trackingUrl: '', vin: '',
      status: 'pending_dispatch',
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBolFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'Upload a PDF BOL', description: 'Only .pdf files are supported.' });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'PDF must be under 15 MB.' });
      return;
    }
    setBolFile(file);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/super-dispatch/parse-bol`, fd, {
        headers: { 'X-Admin-Password': token, 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({
        ...f,
        superDispatchId: data.superDispatchId || f.superDispatchId,
        fromLocation: data.fromLocation || f.fromLocation,
        toLocation: data.toLocation || f.toLocation,
        vehicleType: data.vehicleType || f.vehicleType,
        customerName: data.customerName || f.customerName,
        customerPhone: data.customerPhone || f.customerPhone,
        driverName: data.driverName || f.driverName,
        trackingUrl: data.trackingUrl || f.trackingUrl,
        vin: data.vin || f.vin,
      }));
      toast({
        title: 'BOL parsed ✓',
        description: `Load #${data.superDispatchId} · ${data.fromLocation || '—'} → ${data.toLocation || '—'}${data.driverName ? ` · Driver: ${data.driverName}` : ''}`,
      });
    } catch (err) {
      toast({ title: 'BOL parse failed', description: err?.response?.data?.detail || 'Try manual entry.' });
    } finally {
      setParsing(false);
    }
  };

  const submit = async () => {
    const sd = form.superDispatchId.trim();
    if (!sd) { toast({ title: 'Load ID required' }); return; }
    setBusy(true);
    try {
      let data;
      if (bolFile) {
        // Re-upload the PDF so backend can store it alongside the quote.
        const fd = new FormData();
        fd.append('file', bolFile);
        Object.entries(form).forEach(([k, v]) => { if (v != null && v !== '') fd.append(k, v); });
        const resp = await axios.post(`${API}/admin/super-dispatch/import-bol`, fd, {
          headers: { 'X-Admin-Password': token, 'Content-Type': 'multipart/form-data' },
        });
        data = resp.data;
      } else {
        const resp = await axios.post(`${API}/admin/super-dispatch/import`, form, {
          headers: { 'X-Admin-Password': token },
        });
        data = resp.data;
      }
      toast({
        title: data.created ? 'Load imported ✓' : 'Load updated ✓',
        description: `Tracking code ${data.trackingCode} · SD #${data.superDispatchId}`,
      });
      resetForm();
      setOpen(false);
      if (onImported) onImported();
    } catch (err) {
      toast({ title: 'Import failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0A1628] rounded-2xl border border-[#0A1628] p-5 text-white" data-testid="sd-import-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2AA8E8]/20 flex items-center justify-center">
            <Download className="w-5 h-5 text-[#2AA8E8]" />
          </div>
          <div>
            <h3 className="font-extrabold text-lg">Import Super Dispatch Load</h3>
            <p className="text-xs text-white/70">Drop a BOL PDF from Super Dispatch to auto-fill, or enter the Load ID manually.</p>
          </div>
        </div>
        <Button
          size="sm"
          data-testid="sd-import-toggle"
          onClick={() => setOpen(o => !o)}
          className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2 h-9"
        >
          <Download className="w-3.5 h-3.5" />
          {open ? 'Close' : 'Import Load'}
        </Button>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          {/* BOL drop-zone */}
          <div
            data-testid="sd-bol-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleBolFile(f);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
              dragOver ? 'border-[#2AA8E8] bg-[#2AA8E8]/10' : 'border-white/20 hover:border-[#2AA8E8] hover:bg-white/5'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              data-testid="sd-bol-file-input"
              className="hidden"
              onChange={(e) => handleBolFile(e.target.files?.[0])}
            />
            {parsing ? (
              <div className="flex items-center justify-center gap-2 py-2 text-[#2AA8E8]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-bold">Parsing BOL…</span>
              </div>
            ) : bolFile ? (
              <div data-testid="sd-bol-uploaded" className="flex items-center justify-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <div className="text-left">
                  <div className="font-bold">{bolFile.name}</div>
                  <div className="text-xs text-white/60">{fmtBytes(bolFile.size)} · click to replace</div>
                </div>
              </div>
            ) : (
              <div>
                <Download className="w-7 h-7 text-[#2AA8E8] mx-auto mb-2 rotate-180" />
                <div className="font-bold">Drop BOL PDF here or click to browse</div>
                <div className="text-xs text-white/60 mt-1">We'll auto-fill Load ID · pickup / delivery · vehicle · driver · SD tracking link</div>
              </div>
            )}
          </div>

          {/* Manual / parsed form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#0A1628]">
            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">SD Load ID *</Label>
              <Input
                data-testid="sd-import-id"
                value={form.superDispatchId}
                onChange={(e) => update('superDispatchId', e.target.value)}
                placeholder="21865599"
                className="h-9 mt-1 bg-white"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Pickup</Label>
              <Input data-testid="sd-import-from" value={form.fromLocation} onChange={(e) => update('fromLocation', e.target.value)} placeholder="Los Angeles, CA" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Delivery</Label>
              <Input data-testid="sd-import-to" value={form.toLocation} onChange={(e) => update('toLocation', e.target.value)} placeholder="Phoenix, AZ" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Vehicle</Label>
              <Input data-testid="sd-import-vehicle" value={form.vehicleType} onChange={(e) => update('vehicleType', e.target.value)} placeholder="2012 Ford F350sd" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Trailer</Label>
              <select
                data-testid="sd-import-trailer"
                value={form.trailer}
                onChange={(e) => update('trailer', e.target.value)}
                className="h-9 w-full rounded-md border border-white bg-white px-3 text-sm font-bold text-[#0A1628] mt-1"
              >
                <option value="open">Open</option>
                <option value="enclosed">Enclosed</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">VIN</Label>
              <Input data-testid="sd-import-vin" value={form.vin} onChange={(e) => update('vin', e.target.value)} placeholder="Auto-filled from BOL" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Driver name</Label>
              <Input data-testid="sd-import-driver-name" value={form.driverName} onChange={(e) => update('driverName', e.target.value)} placeholder="Auto-filled from BOL" className="h-9 mt-1 bg-white" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">SD tracking link</Label>
              <Input data-testid="sd-import-tracking-url" value={form.trackingUrl} onChange={(e) => update('trackingUrl', e.target.value)} placeholder="https://bol.superdispatch.com/…" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Customer name</Label>
              <Input data-testid="sd-import-cust-name" value={form.customerName} onChange={(e) => update('customerName', e.target.value)} placeholder="Optional" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Customer email</Label>
              <Input data-testid="sd-import-cust-email" value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} placeholder="Optional" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">Status</Label>
              <select
                data-testid="sd-import-status"
                value={form.status}
                onChange={(e) => update('status', e.target.value)}
                className="h-9 w-full rounded-md border border-white bg-white px-3 text-sm font-bold text-[#0A1628] mt-1"
              >
                {SHIPMENT_STATUS_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              {bolFile && (
                <Button
                  variant="outline"
                  data-testid="sd-import-reset"
                  onClick={resetForm}
                  className="gap-2 border-white/30 text-white hover:bg-white/10"
                >
                  Reset
                </Button>
              )}
              <Button
                data-testid="sd-import-submit"
                onClick={submit}
                disabled={busy || parsing || !form.superDispatchId.trim()}
                className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {bolFile ? 'Confirm & import with BOL' : 'Import to tracking DB'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BROKER_STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'denied', label: 'Denied', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'pending_review', label: 'Pending Review', color: 'bg-slate-50 text-slate-600 border-slate-200' },
];

const EMPTY_BROKER = {
  name: '', mcNumber: '', dotNumber: '', creditStatus: 'pending_review',
  lastCheckedDate: '', daysToPay: '', notes: '',
  contactName: '', contactPhone: '', contactEmail: '',
};

const SUGGESTED_PROMPTS = [
  'How many quotes came in this week?',
  'Which brokers are flagged as high risk by AI?',
  'Show me all training candidates with status pending',
  'Summarize today\'s operations',
  'Which approved brokers haven\'t been AI-scored yet?',
];

const BrainChatPanel = ({ token }) => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi — I\'m Excelence Brain. Ask me anything about your brokers, quotes, applications, or team.', toolCalls: [] },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [expandedTools, setExpandedTools] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setMessages(m => [...m, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const history = messages.filter(m => m.role !== 'system').map(({ role, content }) => ({ role, content }));
      const { data } = await axios.post(
        `${API}/admin/brain/chat`,
        { message: text, history },
        { headers: { 'X-Admin-Password': token } },
      );
      setMessages(m => [...m, { role: 'assistant', content: data.answer, toolCalls: data.toolCalls || [], iterations: data.iterations }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Brain unavailable.';
      setMessages(m => [...m, { role: 'assistant', content: `⚠️ ${detail}`, toolCalls: [] }]);
      toast({ title: 'Brain error', description: detail });
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5EAF2] flex flex-col" data-testid="brain-chat-panel" style={{ height: 'calc(100vh - 320px)', minHeight: '500px' }}>
      <div className="px-5 py-4 border-b border-[#E5EAF2] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-extrabold text-[#0A1628]">Excelence Brain</h3>
            <p className="text-xs text-[#4A5A72]">GPT-4o orchestrator · queries your live data</p>
          </div>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4A5A72]">
          Tools online: brokers · quotes · applications · users
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="brain-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
              m.role === 'user'
                ? 'bg-[#2AA8E8] text-white'
                : 'bg-[#F7F9FC] border border-[#E5EAF2] text-[#0A1628]'
            }`}>
              <div className="text-sm whitespace-pre-wrap">{m.content}</div>
              {m.toolCalls && m.toolCalls.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E5EAF2] space-y-1">
                  <button
                    type="button"
                    onClick={() => setExpandedTools(e => ({ ...e, [i]: !e[i] }))}
                    className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4A5A72] hover:text-[#2AA8E8]"
                    data-testid={`brain-toolcalls-toggle-${i}`}
                  >
                    {expandedTools[i] ? '▾' : '▸'} {m.toolCalls.length} tool call{m.toolCalls.length === 1 ? '' : 's'} · {m.iterations || 1} iter
                  </button>
                  {expandedTools[i] && (
                    <div className="space-y-2">
                      {m.toolCalls.map((tc, j) => (
                        <div key={j} className="bg-white rounded-lg p-2 text-xs border border-[#E5EAF2]">
                          <code className="text-[#2AA8E8] font-bold">{tc.tool}({JSON.stringify(tc.args)})</code>
                          <pre className="mt-1 text-[10px] text-[#4A5A72] overflow-x-auto">{JSON.stringify(tc.result, null, 2).slice(0, 500)}</pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-[#F7F9FC] border border-[#E5EAF2] rounded-2xl px-4 py-3 flex items-center gap-2 text-sm text-[#4A5A72]">
              <Loader2 className="w-4 h-4 animate-spin text-[#2AA8E8]" />
              Thinking & querying data…
            </div>
          </div>
        )}
      </div>

      {/* Suggested prompts (shown when conversation is short) */}
      {messages.length <= 2 && (
        <div className="px-5 pb-3 flex flex-wrap gap-2">
          {SUGGESTED_PROMPTS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => send(p)}
              data-testid={`brain-suggestion-${p.slice(0, 20).replace(/\s/g, '-')}`}
              className="text-xs px-3 py-1.5 rounded-full border border-[#E5EAF2] bg-white text-[#0A1628] hover:border-[#2AA8E8] hover:text-[#2AA8E8] transition-colors"
              disabled={busy}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      <div className="px-5 pb-5 pt-2 border-t border-[#E5EAF2]">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="brain-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask about brokers, quotes, applications…"
            className="flex-1 min-h-[44px] max-h-32 rounded-lg border border-[#E5EAF2] bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#2AA8E8] resize-none"
            rows={1}
            disabled={busy}
          />
          <Button
            data-testid="brain-send"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2 h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
            <span className="hidden sm:inline">Send</span>
          </Button>
        </div>
        <p className="text-[10px] text-[#4A5A72] mt-2">
          AI may make mistakes. Verify financial / safety decisions before acting.
        </p>
      </div>
    </div>
  );
};

const EMPTY_USER_FORM = { email: '', name: '', role: 'viewer', phone: '', password: '' };

const UsersAdminPanel = ({ token, session, onConfirm, closeConfirm }) => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER_FORM);
  const [busy, setBusy] = useState(false);
  const headers = useMemo(() => ({ 'X-Admin-Password': token }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers });
      setUsers(data);
    } catch (err) { toast({ title: 'Load failed', description: err?.response?.data?.detail || '' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing('new'); setForm(EMPTY_USER_FORM); };
  const startEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, role: u.role, phone: u.phone || '', password: '' });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_USER_FORM); };

  const save = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast({ title: 'Email and name are required' }); return; }
    setBusy(true);
    try {
      if (editing === 'new') {
        if (form.password.length < 6) { toast({ title: 'Temp password must be at least 6 characters' }); setBusy(false); return; }
        await axios.post(`${API}/admin/users`, form, { headers });
        toast({ title: 'User created ✓', description: `${form.email} must change their password on first login.` });
      } else {
        const payload = { name: form.name, role: form.role, phone: form.phone };
        if (form.password) payload.password = form.password;  // optional admin reset
        await axios.patch(`${API}/admin/users/${editing.id}`, payload, { headers });
        toast({ title: 'User updated ✓' });
      }
      cancel(); load();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, { active: !u.active }, { headers });
      toast({ title: u.active ? 'User disabled' : 'User enabled' });
      load();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || '' }); }
  };

  const remove = (u) => {
    onConfirm({
      title: 'Are you sure you want to delete this user?',
      description: `${u.name} (${u.email}) will be permanently removed and signed out everywhere.`,
      confirmLabel: 'Delete user',
      variant: 'danger',
      testid: 'delete-user-confirm',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/admin/users/${u.id}`, { headers });
          toast({ title: 'User deleted' });
          load();
        } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' }); }
      },
    });
  };

  const meId = session?.user?.id;

  return (
    <div className="space-y-4" data-testid="users-admin-panel">
      {editing && (
        <div className="bg-white rounded-2xl border border-[#E5EAF2] p-5" data-testid="user-edit-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-extrabold text-[#0A1628]">{editing === 'new' ? 'Add user' : `Edit ${editing.name}`}</h3>
            <Button variant="ghost" size="sm" onClick={cancel} data-testid="user-edit-cancel"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Email *</Label>
              <Input
                data-testid="user-form-email"
                type="email"
                value={form.email}
                disabled={editing !== 'new'}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Full name *</Label>
              <Input data-testid="user-form-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Role</Label>
              <select
                data-testid="user-form-role"
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="h-9 w-full mt-1 rounded-md border border-[#E5EAF2] bg-white px-3 text-sm font-bold text-[#0A1628]"
              >
                {USER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Phone</Label>
              <Input data-testid="user-form-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">
                {editing === 'new' ? 'Temporary password * (min 6 chars)' : 'Reset password (leave empty to skip)'}
              </Label>
              <Input
                data-testid="user-form-password"
                type="text"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder={editing === 'new' ? 'User will be forced to change on first login' : ''}
                className="h-9 mt-1"
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="user-form-save" className="bg-[#0A1628] hover:bg-[#0A1628]/90 text-white gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing === 'new' ? 'Create user' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-extrabold text-[#0A1628]">Team ({users.length})</h3>
          {!editing && (
            <Button onClick={startNew} data-testid="user-add-new" className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2 h-9">
              <Plus className="w-4 h-4" /> Add user
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-center py-10 text-[#4A5A72]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 text-[#4A5A72]"><Users className="w-10 h-10 mx-auto mb-2" /><p className="font-bold text-[#0A1628]">No users yet</p></div>
        ) : (
          <div className="space-y-2">
            {users.map(u => {
              const role = USER_ROLES.find(r => r.value === u.role) || USER_ROLES[4];
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`} className={`flex items-center gap-3 p-3 rounded-lg border flex-wrap ${u.active ? 'border-[#E5EAF2]' : 'border-[#E5EAF2] bg-[#F7F9FC] opacity-70'}`}>
                  <div className="w-9 h-9 rounded-full bg-[#0A1628] text-[#2AA8E8] flex items-center justify-center font-extrabold text-xs shrink-0">
                    {u.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#0A1628] truncate flex items-center gap-2">
                      {u.name}
                      {u.id === meId && <span className="text-[10px] font-bold text-[#2AA8E8] uppercase">You</span>}
                      {u.mustChangePassword && <span className="text-[10px] font-bold text-amber-600 uppercase">Pending first login</span>}
                    </div>
                    <div className="text-xs text-[#4A5A72] truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                  </div>
                  <Pill color="bg-[#0A1628] text-[#2AA8E8] border-[#0A1628]">{role.label}</Pill>
                  <Pill color={u.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                    {u.active ? 'Active' : 'Disabled'}
                  </Pill>
                  <Button variant="outline" size="sm" onClick={() => toggleActive(u)} disabled={u.id === meId} data-testid={`user-toggle-${u.id}`} className="h-8 gap-1.5">
                    {u.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => startEdit(u)} data-testid={`user-edit-${u.id}`} className="h-8 gap-1.5">
                    <Pencil className="w-3.5 h-3.5" /><span className="hidden sm:inline">Edit</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(u)} disabled={u.id === meId} data-testid={`user-delete-${u.id}`} className="h-8 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40">
                    <Trash2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Delete</span>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const BrokersAdminPanel = ({ token, onConfirm, closeConfirm }) => {
  const { toast } = useToast();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {broker} | 'new'
  const [form, setForm] = useState(EMPTY_BROKER);
  const [pwStatus, setPwStatus] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  const headers = useMemo(() => ({ 'X-Admin-Password': token }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/brokers`, { headers }),
        axios.get(`${API}/admin/broker-directory/password-status`, { headers }),
      ]);
      setBrokers(a.data);
      setPwStatus(b.data);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || '' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing('new'); setForm(EMPTY_BROKER); };
  const startEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '',
      mcNumber: b.mcNumber || '',
      dotNumber: b.dotNumber || '',
      creditStatus: b.creditStatus || 'pending_review',
      lastCheckedDate: b.lastCheckedDate || '',
      daysToPay: b.daysToPay ?? '',
      notes: b.notes || '',
      contactName: b.contactName || '',
      contactPhone: b.contactPhone || '',
      contactEmail: b.contactEmail || '',
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_BROKER); };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required' }); return; }
    const payload = {
      ...form,
      daysToPay: form.daysToPay === '' ? null : Number(form.daysToPay),
    };
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/brokers`, payload, { headers });
        toast({ title: 'Broker added ✓' });
      } else {
        await axios.patch(`${API}/admin/brokers/${editing.id}`, payload, { headers });
        toast({ title: 'Broker updated ✓' });
      }
      cancel();
      load();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || '' }); }
  };

  const remove = (b) => {
    onConfirm({
      title: 'Are you sure you want to delete this item?',
      description: `${b.name} will be removed from the broker directory. This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      testid: 'delete-broker-confirm',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/admin/brokers/${b.id}`, { headers });
          toast({ title: 'Broker deleted' });
          load();
        } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' }); }
      },
    });
  };

  const [scoringId, setScoringId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const runAiScore = async (b) => {
    setScoringId(b.id);
    try {
      const { data } = await axios.post(`${API}/admin/brokers/${b.id}/risk-score`, {}, { headers });
      toast({ title: `AI score: ${data.score}/100 · ${data.recommendation}`, description: data.summary });
      setExpandedId(b.id);
      load();
    } catch (err) {
      toast({ title: 'AI scoring failed', description: err?.response?.data?.detail || '' });
    } finally { setScoringId(null); }
  };

  const rotatePassword = async () => {
    if (newPw.length < 6) { toast({ title: 'Password must be at least 6 characters' }); return; }
    setPwBusy(true);
    try {
      await axios.put(`${API}/admin/broker-directory/password`, { newPassword: newPw }, { headers });
      toast({ title: 'Directory password updated ✓', description: 'All active sessions were revoked.' });
      setNewPw('');
      load();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail || '' }); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="brokers-admin-panel">
      {/* Password rotation */}
      <div className="bg-[#0A1628] rounded-2xl p-5 text-white" data-testid="broker-pw-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2AA8E8]/20 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#2AA8E8]" />
            </div>
            <div>
              <h3 className="font-extrabold text-lg">Broker directory password</h3>
              <p className="text-xs text-white/70">
                Current seed: <code className="text-[#D4A24C]">admin1122</code> · {pwStatus?.activeSessions ?? 0} active session{pwStatus?.activeSessions === 1 ? '' : 's'}
                {pwStatus?.updatedAt ? ` · Last changed ${fmtDate(pwStatus.updatedAt)}` : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:items-end">
          <div className="flex-1">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-white/80">New password</Label>
            <Input
              data-testid="broker-new-password"
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="At least 6 characters"
              className="h-9 mt-1 bg-white text-[#0A1628]"
            />
          </div>
          <Button
            data-testid="broker-rotate-password"
            onClick={rotatePassword}
            disabled={pwBusy || newPw.length < 6}
            className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2 h-9"
          >
            {pwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save & revoke all sessions
          </Button>
        </div>
      </div>

      {/* Add/edit form */}
      {editing && (
        <div className="bg-white rounded-2xl border border-[#E5EAF2] p-5" data-testid="broker-edit-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-extrabold text-[#0A1628]">{editing === 'new' ? 'Add broker' : `Edit ${editing.name}`}</h3>
            <Button variant="ghost" size="sm" onClick={cancel} data-testid="broker-edit-cancel"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Company name *</Label>
              <Input data-testid="broker-form-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">MC number</Label>
              <Input data-testid="broker-form-mc" value={form.mcNumber} onChange={(e) => setForm(f => ({ ...f, mcNumber: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">DOT number</Label>
              <Input data-testid="broker-form-dot" value={form.dotNumber} onChange={(e) => setForm(f => ({ ...f, dotNumber: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Credit status</Label>
              <select
                data-testid="broker-form-status"
                value={form.creditStatus}
                onChange={(e) => setForm(f => ({ ...f, creditStatus: e.target.value }))}
                className="h-9 w-full mt-1 rounded-md border border-[#E5EAF2] bg-white px-3 text-sm font-bold text-[#0A1628]"
              >
                {BROKER_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Avg days to pay</Label>
              <Input data-testid="broker-form-dtp" type="number" value={form.daysToPay} onChange={(e) => setForm(f => ({ ...f, daysToPay: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Last checked</Label>
              <Input data-testid="broker-form-last-checked" type="date" value={form.lastCheckedDate} onChange={(e) => setForm(f => ({ ...f, lastCheckedDate: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Contact name</Label>
              <Input data-testid="broker-form-contact-name" value={form.contactName} onChange={(e) => setForm(f => ({ ...f, contactName: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Contact phone</Label>
              <Input data-testid="broker-form-contact-phone" value={form.contactPhone} onChange={(e) => setForm(f => ({ ...f, contactPhone: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Contact email</Label>
              <Input data-testid="broker-form-contact-email" type="email" value={form.contactEmail} onChange={(e) => setForm(f => ({ ...f, contactEmail: e.target.value }))} className="h-9 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4A5A72]">Notes</Label>
              <textarea
                data-testid="broker-form-notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full mt-1 rounded-md border border-[#E5EAF2] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#2AA8E8]"
                rows={3}
                placeholder="Credit history, payment quirks, preferred lanes…"
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button variant="outline" onClick={cancel}>Cancel</Button>
              <Button onClick={save} data-testid="broker-form-save" className="bg-[#0A1628] hover:bg-[#0A1628]/90 text-white gap-2">
                <Save className="w-4 h-4" />{editing === 'new' ? 'Add broker' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* List + add */}
      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-extrabold text-[#0A1628]">Brokerage partners ({brokers.length})</h3>
          {!editing && (
            <Button onClick={startNew} data-testid="broker-add-new" className="bg-[#2AA8E8] hover:bg-[#2AA8E8]/90 text-white gap-2 h-9">
              <Plus className="w-4 h-4" /> Add broker
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-center py-10 text-[#4A5A72]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : brokers.length === 0 ? (
          <div className="text-center py-10">
            <Building2 className="w-10 h-10 text-[#4A5A72] mx-auto mb-2" />
            <p className="font-bold text-[#0A1628]">No brokers yet</p>
            <p className="text-sm text-[#4A5A72]">Add your first brokerage partner above.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {brokers.map(b => {
              const meta = BROKER_STATUS_OPTIONS.find(s => s.value === b.creditStatus) || BROKER_STATUS_OPTIONS[3];
              const aiBadge = b.aiRiskScore != null ? {
                color: b.aiRiskLevel === 'low' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : b.aiRiskLevel === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-red-50 text-red-700 border-red-200',
                label: `AI ${b.aiRiskScore}/100`,
              } : null;
              const isExpanded = expandedId === b.id;
              return (
                <div key={b.id} data-testid={`broker-row-${b.id}`} className="rounded-lg border border-[#E5EAF2] hover:border-[#2AA8E8]/40">
                  <div className="flex items-center gap-3 p-3 flex-wrap">
                    <Building2 className="w-4 h-4 text-[#4A5A72] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#0A1628] truncate">{b.name}</div>
                      <div className="text-xs text-[#4A5A72]">
                        {b.mcNumber && <>MC {b.mcNumber}</>}
                        {b.dotNumber && <> · DOT {b.dotNumber}</>}
                        {b.daysToPay != null && <> · {b.daysToPay}d to pay</>}
                      </div>
                    </div>
                    <Pill color={meta.color}>{meta.label}</Pill>
                    {aiBadge && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : b.id)}
                        data-testid={`broker-ai-badge-${b.id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${aiBadge.color} hover:opacity-80`}
                      >
                        🧠 {aiBadge.label}
                      </button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => runAiScore(b)}
                      disabled={scoringId === b.id}
                      data-testid={`broker-ai-run-${b.id}`}
                      className="h-8 gap-1.5 border-[#2AA8E8] text-[#2AA8E8] hover:bg-[#2AA8E8] hover:text-white"
                    >
                      {scoringId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : '🧠'}
                      <span className="hidden sm:inline">{b.aiRiskScore != null ? 'Re-score' : 'AI Score'}</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => startEdit(b)} data-testid={`broker-edit-${b.id}`} className="h-8 gap-1.5">
                      <Pencil className="w-3.5 h-3.5" /><span className="hidden sm:inline">Edit</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(b)} data-testid={`broker-delete-${b.id}`} className="h-8 gap-1.5 text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Delete</span>
                    </Button>
                  </div>
                  {isExpanded && b.aiRiskScore != null && (
                    <div data-testid={`broker-ai-details-${b.id}`} className="border-t border-[#E5EAF2] bg-[#F7F9FC] p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4A5A72]">AI risk analysis · GPT-4o</div>
                        <div className="text-xs text-[#4A5A72]">
                          {b.aiRiskUpdatedAt ? `Updated ${fmtDate(b.aiRiskUpdatedAt)}` : ''}
                          {b.aiRiskRecommendation && <> · <span className="font-bold uppercase">{b.aiRiskRecommendation}</span></>}
                        </div>
                      </div>
                      <p className="text-sm text-[#0A1628] font-bold">{b.aiRiskSummary}</p>
                      {Array.isArray(b.aiRiskFactors) && b.aiRiskFactors.length > 0 && (
                        <ul className="text-xs text-[#4A5A72] list-disc pl-5 space-y-1">
                          {b.aiRiskFactors.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const PricingSettingsPanel = ({ token }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/pricing-settings`, {
          headers: { 'X-Admin-Password': token },
        });
        setSettings(data);
      } catch (err) {
        toast({ title: 'Failed to load pricing settings', description: err?.response?.data?.detail || '' });
      } finally { setLoading(false); }
    })();
  }, [token, toast]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/pricing-settings`, settings, {
        headers: { 'X-Admin-Password': token },
      });
      setSettings(data);
      toast({ title: 'Pricing settings saved ✓', description: 'All new quotes will use the updated rates.' });
    } catch (err) {
      toast({ title: 'Failed to save', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin inline text-[#2AA8E8]" /></div>;

  const updateRate = (trailer, i, key, val) => {
    const v = parseFloat(val) || 0;
    setSettings(s => ({
      ...s,
      rates: { ...s.rates, [trailer]: s.rates[trailer].map((t, idx) => idx === i ? { ...t, [key]: v } : t) },
    }));
  };
  const updateVeh = (k, val) => {
    setSettings(s => ({ ...s, vehicleSurcharges: { ...s.vehicleSurcharges, [k]: parseFloat(val) || 0 } }));
  };
  const updateCond = (k, val) => {
    setSettings(s => ({ ...s, conditionSurcharges: { ...s.conditionSurcharges, [k]: parseFloat(val) || 0 } }));
  };
  const updateMin = (k, val) => {
    setSettings(s => ({ ...s, minTotal: { ...s.minTotal, [k]: parseFloat(val) || 0 } }));
  };

  return (
    <div className="space-y-6" data-testid="pricing-settings-panel">
      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-extrabold text-[#0A1628] flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#D4A24C]" /> Pricing Settings</h3>
            <p className="text-sm text-[#4A5A72] mt-1">Edit per-mile rates, surcharges and transit formulas. Changes apply immediately to all new quotes.</p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="pricing-save-btn" className="btn-accent gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </Button>
        </div>
      </div>

      {/* Per-mile rates */}
      {['open', 'enclosed'].map(trailer => (
        <div key={trailer} className="bg-white rounded-2xl border border-[#E5EAF2] p-6" data-testid={`pricing-rates-${trailer}`}>
          <h4 className="font-extrabold text-[#0A1628] mb-4 capitalize">{trailer} trailer · per-mile rates</h4>
          <div className="space-y-3">
            {settings.rates[trailer].map((t, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label className="text-xs text-[#4A5A72]">Tier</Label>
                  <div className="h-10 flex items-center text-sm font-bold text-[#0A1628]">{t.label}</div>
                </div>
                <div>
                  <Label className="text-xs text-[#4A5A72]">Min $/mi</Label>
                  <Input type="number" step="0.01" value={t.min} onChange={e => updateRate(trailer, i, 'min', e.target.value)} data-testid={`rate-${trailer}-${i}-min`} />
                </div>
                <div>
                  <Label className="text-xs text-[#4A5A72]">Max $/mi</Label>
                  <Input type="number" step="0.01" value={t.max} onChange={e => updateRate(trailer, i, 'max', e.target.value)} data-testid={`rate-${trailer}-${i}-max`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Vehicle surcharges */}
      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
        <h4 className="font-extrabold text-[#0A1628] mb-1">Vehicle surcharges</h4>
        <p className="text-xs text-[#4A5A72] mb-4">Flat $ added (or subtracted) based on vehicle type.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(settings.vehicleSurcharges).map(([k, v]) => (
            <div key={k}>
              <Label className="text-xs text-[#4A5A72]">{k}</Label>
              <Input type="number" step="10" value={v} onChange={e => updateVeh(k, e.target.value)} data-testid={`surcharge-vehicle-${k.toLowerCase().replace(/[^a-z]/g, '-')}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Condition surcharges */}
      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
        <h4 className="font-extrabold text-[#0A1628] mb-1">Condition surcharges · per mile</h4>
        <p className="text-xs text-[#4A5A72] mb-4">$ added <strong>per mile</strong> based on vehicle condition (scales with distance). e.g. 0.50 = +$0.50/mi for non-running vehicles.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(settings.conditionSurcharges).map(([k, v]) => (
            <div key={k}>
              <Label className="text-xs text-[#4A5A72] capitalize">{k} · $/mile</Label>
              <Input type="number" step="0.05" value={v} onChange={e => updateCond(k, e.target.value)} data-testid={`surcharge-cond-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Minimum floor prices + transit formula */}
      <div className="bg-white rounded-2xl border border-[#E5EAF2] p-6">
        <h4 className="font-extrabold text-[#0A1628] mb-4">Minimum totals & transit formula</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#4A5A72]">Min total · Open</Label>
            <Input type="number" value={settings.minTotal.open} onChange={e => updateMin('open', e.target.value)} data-testid="min-total-open" />
          </div>
          <div>
            <Label className="text-xs text-[#4A5A72]">Min total · Enclosed</Label>
            <Input type="number" value={settings.minTotal.enclosed} onChange={e => updateMin('enclosed', e.target.value)} data-testid="min-total-enclosed" />
          </div>
          <div>
            <Label className="text-xs text-[#4A5A72]">Avg mph per dispatch day</Label>
            <Input type="number" value={settings.avgMphPerDay} onChange={e => setSettings(s => ({ ...s, avgMphPerDay: parseFloat(e.target.value) || 0 }))} data-testid="avg-mph" />
            <p className="text-[10px] text-[#4A5A72] mt-1">Drives transit window. 450 is typical for HOS-compliant car haulers.</p>
          </div>
        </div>
      </div>

      <SmsSettingsPanel token={token} />
    </div>
  );
};

export default AdminPage;
